import {
  Flex,
  Heading,
  InlineList,
  ScreenReaderContent,
  Tabs,
  Text,
  TruncateText,
  View,
} from "@instructure/ui";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { Card } from "../../components/Card";

import Skeleton from "react-loading-skeleton";
import { ContextReflections } from "../../components/Reflection/ContextReflections";
import { CurriculumMap } from "../CurriculumMap";

import { useTranslation } from "react-i18next";
import { Actions } from "../../components/Action/Actions";
import Assignment from "../../components/Assignment";
import Cycle from "../../components/CyclesAndGoals/Cycle";
import { CyclesService } from "../../services/CyclesService";
import { ResultService } from "../../services/ResultService";
import { SectionService } from "../../services/SectionService";
import AssessorsCard from "./AssessorsCard";
import CourseChart from "./CourseChart";

const Course = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const [storedState, setStoredState] = useState(null);

  const [linkDetails, setLinkDetails] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [cycle, setCycle] = useState(null);
  const [sectionResults, setSectionResults] = useState(null);
  const [dashboardFilters, setDashboardFilters] = useState(null);

  useEffect(() => {
    if (location.state) {
      setStoredState(location.state);
      setLinkDetails(location.state.offering);
    } else {
      const tmp = JSON.parse(localStorage.getItem("courseState"));
      setStoredState(tmp);
      if (tmp.offering) {
        setLinkDetails(tmp.offering);
      } else {
        setLinkDetails(null);
      }
    }

    try {
      localStorage.removeItem("courseState");
    } catch (err) {
      console.error(err.message);
    }
  }, []);

  useEffect(() => {
    if (storedState) {
      let context_id = null;
      context_id = storedState.offering.evidence[0].course_id; // the canvas section id or -1 for external assessment
      if (context_id === -1) {
        // external assessment
        const assessment_id = storedState.offering.evidence[0].assessment_id;
        const assessment = storedState.selectedAssessmentPlan.sections
          .flatMap((section) => section.assessments)
          .find((assessment) => assessment.id === assessment_id);
        context_id = assessment.purl_id; // get section by PURL id for external assessment
      }
      const term_id = storedState.offering.evidence[0].term_id;

      SectionService.getSectionsByCourseAndTermIds(context_id, [term_id]).then(
        (section) => {
          linkDetails.section = section ?? {};
          const canvas_or_external_section_id =
            storedState.offering.evidence[0].course_id !== -1
              ? context_id
              : section.ddl_id;
          ResultService.getResultsBySectionCanvasIds([
            canvas_or_external_section_id,
          ]).then((results) => {
            const assignmentResults = [];
            if (results && results[canvas_or_external_section_id]) {
              for (const key of Object.keys(
                results[canvas_or_external_section_id]
              )) {
                const obj = {};
                obj[key] = results[canvas_or_external_section_id][key];
                assignmentResults.push(obj);
              }
            }
            setSectionResults(assignmentResults);
          });
        }
      );
      setDashboardFilters({
        ...storedState.dashboardFilters,
        subject: storedState.offering.academic_subject.title,
        LOType: "PSLO",
      });

      CyclesService.getCycles().then((cycles) => {
        setCycle(cycles.find((cycle) => cycle.id === storedState.cycle.id));
      });
    }
  }, [storedState]);

  const handleTabChange = (e, { index }) => {
    setSelectedIndex(index);
  };

  return (
    <View as="div">
      {cycle && linkDetails && (
        <TruncateText maxLines="auto">
          <div style={{ borderBottom: "1px solid #ccc", paddingBottom: 5 }}>
            <InlineList delimiter="arrow" size="large">
              <InlineList.Item>
                <Text size="x-large">
                  <Link style={{ color: "inherit" }} state={{ cycle }} to="/">
                    {t("dashboard.title")}
                  </Link>
                </Text>
              </InlineList.Item>
              <InlineList.Item>
                <Text size="x-large">
                  {linkDetails.section && linkDetails.section.name
                    ? `${linkDetails.code}: ${linkDetails.section.name}, ${linkDetails.term}`
                    : `${linkDetails.code}, ${linkDetails.term}`}
                </Text>
                <ScreenReaderContent>
                  <Heading as="h1" level="h2">
                    {linkDetails.section && linkDetails.section.name
                      ? `${linkDetails.code}: ${linkDetails.section.name}, ${linkDetails.term}`
                      : `${linkDetails.code}, ${linkDetails.term}`}
                  </Heading>
                </ScreenReaderContent>
              </InlineList.Item>
            </InlineList>
          </div>
        </TruncateText>
      )}

      {storedState &&
      linkDetails &&
      cycle &&
      sectionResults &&
      dashboardFilters ? (
        <>
          <Flex
            wrap="wrap"
            alignItems="start"
            gap="medium"
            margin="small 0 medium 0"
          >
            <Flex.Item shouldGrow shouldShrink width="750px">
              <CourseChart
                offering={storedState.offering}
                filters={dashboardFilters}
                setFilters={setDashboardFilters}
              />
            </Flex.Item>

            <Flex.Item size="25%" shouldGrow>
              <Cycle cycle={cycle} pathname={location && location.pathname} />
              <AssessorsCard offering={storedState.offering} />
            </Flex.Item>
          </Flex>
          <Card style={{ height: "auto" }}>
            <Tabs onRequestTabChange={handleTabChange}>
              <Tabs.Panel
                id="assignments"
                renderTitle={t("assignment.assignments")}
                isSelected={selectedIndex === 0}
              >
                <Flex wrap="wrap" gap="small">
                  {storedState?.offering?.evidence.map((assignment, index) => (
                    <Flex.Item size="49%" key={index} align="start">
                      <Assignment
                        assignment={assignment}
                        assessment={storedState.selectedAssessmentPlan.sections
                          .flatMap((section) => section.assessments)
                          .find(
                            (assessment) =>
                              assessment.id === assignment.assessment_id
                          )}
                        term={storedState.offering.term}
                        sectionAssignmentResults={sectionResults}
                        barChartIndex={index}
                      />
                    </Flex.Item>
                  ))}
                </Flex>
              </Tabs.Panel>
              <Tabs.Panel
                id="curriculumMap"
                renderTitle={t("curriculumMap.headerTitle")}
                isSelected={selectedIndex === 1}
              >
                <CurriculumMap
                  selectedAssessmentPlan={storedState.selectedAssessmentPlan}
                  isDashboard={true}
                />
              </Tabs.Panel>
              <Tabs.Panel
                id="reflections"
                renderTitle={t("reflections.reflections")}
                isSelected={selectedIndex === 2}
              >
                <ContextReflections
                  contextId={linkDetails.id}
                  contextName={linkDetails.code + ", " + linkDetails.term}
                />
              </Tabs.Panel>
              <Tabs.Panel
                id="actions"
                renderTitle={t("actions.actions")}
                isSelected={selectedIndex === 3}
              >
                <Actions contextId={linkDetails.id} />
              </Tabs.Panel>
            </Tabs>
          </Card>
        </>
      ) : (
        <>
          <Flex wrap="wrap" gap="medium" margin="small 0 medium 0">
            <Flex.Item shouldGrow shouldShrink width="750px">
              <Skeleton height={400} />
            </Flex.Item>

            <Flex.Item size="25%" shouldGrow>
              <Skeleton height={192} />
              <Skeleton height={192} style={{ marginTop: "1rem" }} />
            </Flex.Item>
          </Flex>
          <Skeleton height={362} />
        </>
      )}
    </View>
  );
};
export default Course;
